import React, { useEffect, useState, useCallback } from "react";
import {
  MdReceiptLong, MdRefresh, MdSearch, MdDownload, MdDescription,
  MdAccountBalanceWallet, MdCheckCircle, MdPendingActions,
} from "react-icons/md";
import { useAuth } from "contexts/AuthContext";
import {
  getInvoices, getUnpaidAmount, downloadInvoicePdf, saveBlob,
} from "services/invoicesService";

// ── Invoice status registry ────────────────────────────────────────────────────
const STATUS = {
  "en cours": { label: "En cours", color: "bg-yellow-100 text-yellow-700" },
  "closed":   { label: "Clôturée", color: "bg-blue-100 text-blue-700"     },
  "payed":    { label: "Payée",    color: "bg-green-100 text-green-700"   },
};

const STATUS_FILTERS = [
  { value: "",        label: "Tous les statuts" },
  { value: "en cours", label: "En cours" },
  { value: "closed",   label: "Clôturées" },
  { value: "payed",    label: "Payées" },
];

function statusMeta(status) {
  return STATUS[status] || { label: status || "—", color: "bg-gray-100 text-gray-600" };
}

function money(value) {
  return Number(value || 0).toFixed(3);
}

function fmtDay(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function period(invoice) {
  return `${fmtDay(invoice.start_date)} → ${fmtDay(invoice.end_date)}`;
}

function safeFileName(ref, id, detailed) {
  const base = String(ref || `facture-${id}`).replace(/[^a-z0-9-_]+/gi, "-");
  return `${base}${detailed ? "-detaillee" : ""}.pdf`;
}

// ── Stat card ───────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, accent }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl bg-white p-4 shadow-sm dark:bg-navy-800">
      <span className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl ${accent}`}>
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-xs font-medium text-gray-400">{label}</p>
        <p className="truncate text-xl font-bold text-navy-700 dark:text-white">{value}</p>
      </div>
    </div>
  );
}

// ── Main Invoices page ──────────────────────────────────────────────────────────
export default function Invoices() {
  const { restaurantId } = useAuth();

  const [invoices, setInvoices] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [unpaid,   setUnpaid]   = useState(0);
  const [status,   setStatus]   = useState("");
  const [search,   setSearch]   = useState("");
  const [downloading, setDownloading] = useState(null); // `${id}-${detailed}`
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    setError(null);
    try {
      const params = status ? { status } : {};
      const [list, amount] = await Promise.all([
        getInvoices(restaurantId, params),
        getUnpaidAmount(restaurantId).catch(() => ({ amount: 0 })),
      ]);
      setInvoices(Array.isArray(list) ? list : list.invoices || []);
      setUnpaid(amount?.amount ?? 0);
    } catch {
      setError("Impossible de charger les factures. Réessayez.");
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, status]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async (invoice, detailed) => {
    const key = `${invoice.id}-${detailed}`;
    setDownloading(key);
    try {
      const blob = await downloadInvoicePdf(restaurantId, invoice.id, detailed);
      saveBlob(blob, safeFileName(invoice.ref, invoice.id, detailed));
    } catch {
      setError("Le téléchargement a échoué. Réessayez.");
    } finally {
      setDownloading(null);
    }
  };

  const query = search.trim().toLowerCase();
  const filtered = query
    ? invoices.filter((inv) => String(inv.ref || inv.id).toLowerCase().includes(query))
    : invoices;

  const paidCount = invoices.filter((inv) => inv.status === "payed").length;

  return (
    <div className="mt-3">
      {/* Header */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-2xl font-bold text-navy-700 dark:text-white">
          <MdReceiptLong className="h-7 w-7 text-brand-500" />
          Mes factures
        </h2>
        <button
          onClick={() => load()}
          className="flex items-center gap-1.5 rounded-xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
        >
          <MdRefresh className="h-4 w-4" />
          Actualiser
        </button>
      </div>

      {/* Summary cards */}
      <div className="mb-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          icon={<MdReceiptLong className="h-5 w-5 text-brand-600" />}
          accent="bg-brand-100"
          label="Total factures"
          value={invoices.length}
        />
        <StatCard
          icon={<MdAccountBalanceWallet className="h-5 w-5 text-red-500" />}
          accent="bg-red-100"
          label="Montant impayé"
          value={`${money(unpaid)} TND`}
        />
        <StatCard
          icon={<MdCheckCircle className="h-5 w-5 text-green-600" />}
          accent="bg-green-100"
          label="Factures payées"
          value={paidCount}
        />
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl bg-white p-4 shadow-sm dark:bg-navy-800">
        <div>
          <label className="mb-1 block text-xs font-semibold text-gray-400">Statut</label>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-navy-700 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
          >
            {STATUS_FILTERS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <label className="mb-1 block text-xs font-semibold text-gray-400">Référence</label>
          <div className="relative">
            <MdSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par référence..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full min-w-[160px] rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-navy-700 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600 dark:bg-red-900/20">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-2xl bg-white shadow-sm dark:bg-navy-800">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-20 text-gray-400">
            <MdPendingActions className="h-10 w-10" />
            <p className="text-sm">Aucune facture pour le moment</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-100 text-xs uppercase text-gray-400 dark:border-navy-700">
                  <th className="px-5 py-3 font-semibold">Référence</th>
                  <th className="px-5 py-3 font-semibold">Période</th>
                  <th className="px-5 py-3 text-right font-semibold">Montant</th>
                  <th className="px-5 py-3 font-semibold">Statut</th>
                  <th className="px-5 py-3 text-right font-semibold">Téléchargement</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((invoice) => {
                  const st = statusMeta(invoice.status);
                  const dlSimple   = downloading === `${invoice.id}-false`;
                  const dlDetailed = downloading === `${invoice.id}-true`;
                  return (
                    <tr
                      key={invoice.id}
                      className="border-b border-gray-50 transition-colors hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700"
                    >
                      <td className="px-5 py-3 font-bold text-navy-700 dark:text-white">
                        {invoice.ref || `#${invoice.id}`}
                      </td>
                      <td className="px-5 py-3 text-gray-500 dark:text-gray-400">{period(invoice)}</td>
                      <td className="px-5 py-3 text-right font-semibold text-navy-700 dark:text-white">
                        {money(invoice.amount)} TND
                      </td>
                      <td className="px-5 py-3">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${st.color}`}>
                          {st.label}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleDownload(invoice, false)}
                            disabled={dlSimple}
                            className="flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-brand-600 disabled:opacity-50"
                          >
                            {dlSimple ? (
                              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            ) : (
                              <MdDownload className="h-3.5 w-3.5" />
                            )}
                            Bon
                          </button>
                          <button
                            onClick={() => handleDownload(invoice, true)}
                            disabled={dlDetailed}
                            className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-navy-700 hover:bg-gray-50 disabled:opacity-50 dark:border-navy-600 dark:text-white dark:hover:bg-navy-700"
                          >
                            {dlDetailed ? (
                              <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
                            ) : (
                              <MdDescription className="h-3.5 w-3.5" />
                            )}
                            Détaillée
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
